import { UseQueryResult } from '@tanstack/react-query';
import i18next from 'i18next';
import { ApiException, ProblemDetails } from 'services/api/api-client.types';
import { useEffect, useRef } from 'react';
import { showErrorToast } from 'components/uikit/toast/showToast';

export const NetworkError = 'Network Error';

export type ErrorToStringOptions = {
  removePropertyNames?: boolean;
};

/*
Converts exception object to readable string.
Handles ASP.NET Core validation errors (ProblemDetails), and other backend errors.
Returns 'Network Error' in case of network errors.
Returns 'Unauthorized' in case of 401
Returns 'Access Denied' in case of 403.
(errors are mentioned here for localization purposes)
 */
export function errorToString(
  error: any,
  options?: ErrorToStringOptions,
): string {
  // error could be:
  // - strongly-typed error (if server-side action is decorated with
  // [ProducesResponseType(400, Type = typeof(ValidationProblemDetails))]
  // - untyped error, in which case `error.response` will be populated with response in JSON.
  const errorResponseData = error.response?.data || error.response || error;
  const errors = errorResponseData?.errors;

  let overallError = convertToErrorStringInternal(error);

  if (errors && Object.keys(errors).length) {
    let formErrorsCombined = '';
    // Some field-bound error (e.g. `user with same Name already exists in DB`)
    // (similar code exists in useErrorHanler.ts, don't forget to change it as well)
    Object.keys(errors).forEach((key) => {
      const keyWithout$ = key.startsWith('$.') ? key.substring(2) : key;
      const camelCaseName =
        keyWithout$.charAt(0).toLowerCase() + keyWithout$.slice(1);
      const errorValue = errors[key];
      const errorString = Array.isArray(errorValue)
        ? errorValue.join('; ')
        : errorValue;
      if (key.includes('.')) {
        key = key.substring(key.lastIndexOf('.') + 1);
      }
      if (options?.removePropertyNames) {
        formErrorsCombined = formErrorsCombined + `${errorString}\n`;
      } else {
        formErrorsCombined =
          formErrorsCombined + `${camelCaseName}: ${errorString}\n`;
      }
    });

    if (overallError === 'One or more validation errors occurred.') {
      // it doesn't make sense to display this error
      overallError = '';
    }

    if (overallError) {
      overallError = overallError + '\n' + formErrorsCombined;
    } else {
      overallError = formErrorsCombined;
    }
  }
  return overallError.trim();
}

export function convertToErrorStringInternal(error: any): string {
  const errorResponseData = error.response || error;
  const responseDetail = errorResponseData?.detail;

  if (error.status === 401) {
    return i18next.t('Error_Unauthorized');
  }
  if (error.status === 403) {
    return i18next.t('Error_AccessDenied');
  }
  if (error.status === 504) {
    return i18next.t('Error_GatewayTimeout');
  }

  if (ApiException.isApiException(error)) {
    error = error.response as any as ProblemDetails;
  }

  if (error.type === 'urn:mccsoft:external-service-is-unavailable') {
    return i18next.t('Error_ExternalServiceUnavailable');
  }

  if (error.code === 'CSS_CHUNK_LOAD_FAILED') {
    return NetworkError;
  }
  if (error.name === 'ChunkLoadError') {
    return NetworkError;
  }
  if (error.message?.includes('fetch dynamically imported module')) {
    return NetworkError;
  }
  if (error.message?.includes("Cannot read property 'status' of undefined")) {
    // nswag generated client throws it when there's no response
    return NetworkError;
  }

  if (responseDetail) {
    // General server-side error not related to certain field (e.g. `Access Denied`)
    return responseDetail;
  }
  if (error.message) {
    // e.g. Network Error
    console.log('Error:', error, JSON.stringify(error));
    return error.message;
  } else if (error.title) {
    return error.title;
  } else if (typeof error.data === 'string') {
    // when Route corresponding to the current URL is not found,
    //{"status":404,"statusText":"Not Found","internal":true,"data":"Error: No route matches URL \"/tabs/_5j485gcqq/documents/patient/174803\"","error":{}}
    return error.data;
  } else if (typeof error === 'string') {
    return error;
  }

  if (error.error_description) return error.error_description;

  const unknownError = JSON.stringify(error);
  console.log('Unknown Error:', error, unknownError);
  return unknownError;
}

export const useQueryErrorAlert = (
  query: UseQueryResult<any, any>,
  errorMessage?: string | null,
  action?: () => any,
) => {
  useEffect(() => {
    if (query.isError) {
      showErrorToast(errorMessage ?? errorToString(query.error));
      action?.();
    }
  }, [query.isError, query.isLoading, query.error]);
};

export const useHandleQueryError = (
  query: UseQueryResult<any, any>,
  handler: () => Promise<any> | void,
  statusCode?: number,
) => {
  const handledRef = useRef(false);

  useEffect(() => {
    if (query.isError && !handledRef.current) {
      if (statusCode && query.error?.status !== statusCode) throw query.error;

      handledRef.current = true;

      (async () => {
        await handler();
      })();
    }

    if (!query.isError) {
      handledRef.current = false;
    }
  }, [query.isError, query.error?.status, statusCode, handler]);
};
